import { Link } from 'react-router-dom'
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline'
import { ensureAbsoluteWebUrl } from '@/utils/absoluteWebUrl'
import { usePlatformBranding } from '@/hooks/usePlatformBranding'

export type ShopFooterVariant = 'full' | 'minimal' | 'links'

interface ShopFooterProps {
  variant?: ShopFooterVariant
  /** Site page links (e.g. from published pages with show_in_nav); used when variant is links */
  navLinks?: { to: string; label: string }[]
  config: {
    name: string
    logo_url?: string
    address?: string
    phone?: string
    email?: string
    facebook_url?: string
    whatsapp_url?: string
  }
  basePath: string
}

/** WhatsApp icon (brand) */
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

/** Facebook icon (brand) */
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function PoweredByShopQuay() {
  const { website_url } = usePlatformBranding()
  const href = website_url || 'https://www.shopquay.com'
  return (
    <>
      Powered by{' '}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline decoration-current/40 underline-offset-2 hover:decoration-current"
      >
        ShopQuay
      </a>{' '}
      Limited
    </>
  )
}

function CopyrightBar({ name }: { name: string }) {
  return (
    <div className="border-t border-slate-700 py-4">
      <div className="container mx-auto px-6 text-center text-sm text-slate-400">
        © {new Date().getFullYear()} Copyrights by {name}
        {' | '}
        <PoweredByShopQuay />
      </div>
    </div>
  )
}

export function ShopFooter({ variant = 'full', navLinks = [], config, basePath }: ShopFooterProps) {
  const { name, logo_url, address, phone, email, facebook_url, whatsapp_url } = config
  const waHref = ensureAbsoluteWebUrl(whatsapp_url)
  const fbHref = ensureAbsoluteWebUrl(facebook_url)
  const hasContact = address || phone || email
  const hasSocial = fbHref || waHref

  if (variant === 'minimal') {
    return (
      <footer className="border-t border-slate-200 bg-slate-100 text-slate-600">
        <div className="container mx-auto px-6 py-4 text-center text-sm">
          © {new Date().getFullYear()} {name}
          <span className="text-slate-400">
            {' '}
            · <PoweredByShopQuay />
          </span>
        </div>
      </footer>
    )
  }

  if (variant === 'links') {
    return (
      <footer className="bg-slate-800 text-white">
        <div className="container mx-auto px-6 py-8">
          {navLinks.length > 0 && (
            <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              {navLinks.map((item) => (
                <Link key={item.to} to={item.to} className="text-slate-300 hover:text-white">
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
          {hasSocial && (
            <div className={`flex justify-center gap-3 ${navLinks.length > 0 ? 'mt-6' : ''}`}>
              {waHref && (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                </a>
              )}
              {fbHref && (
                <a
                  href={fbHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
                  aria-label="Facebook"
                >
                  <FacebookIcon className="h-5 w-5" />
                </a>
              )}
            </div>
          )}
        </div>
        <CopyrightBar name={name} />
      </footer>
    )
  }

  return (
    <footer className="bg-slate-800 text-white">
      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link to={basePath} className="inline-block">
              {logo_url ? (
                <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-white p-2">
                  <img src={logo_url} alt={name} className="h-full w-full object-contain" />
                </div>
              ) : (
                <span className="text-xl font-semibold">{name}</span>
              )}
            </Link>
          </div>

          {/* Middle: Contact info */}
          {hasContact && (
            <div className="flex-1 space-y-3">
              <h3 className="text-lg font-semibold">{name}</h3>
              {address && (
                <div className="flex items-start gap-2 text-sm text-slate-300">
                  <MapPinIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-400" />
                  <span>{address}</span>
                </div>
              )}
              {phone && (
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <PhoneIcon className="h-5 w-5 flex-shrink-0 text-slate-400" />
                  <a href={`tel:${phone}`} className="hover:text-white">
                    {phone}
                  </a>
                </div>
              )}
              {email && (
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <EnvelopeIcon className="h-5 w-5 flex-shrink-0 text-slate-400" />
                  <a href={`mailto:${email}`} className="hover:text-white">
                    {email}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Right: Social/contact icons */}
          {hasSocial && (
            <div className="flex flex-shrink-0 gap-3">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
                  aria-label="Call"
                >
                  <PhoneIcon className="h-5 w-5" />
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
                  aria-label="Email"
                >
                  <EnvelopeIcon className="h-5 w-5" />
                </a>
              )}
              {waHref && (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                </a>
              )}
              {fbHref && (
                <a
                  href={fbHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
                  aria-label="Facebook"
                >
                  <FacebookIcon className="h-5 w-5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <CopyrightBar name={name} />
    </footer>
  )
}
